import type { ReactNode } from 'react';
import { Navigate, Route, type RouteObject, Routes, useRoutes } from 'react-router-dom';
import Login from './shell/pages/Login';
import Register from './shell/pages/Register';
import Home from './shell/pages/Home';
import Password from './shell/pages/Password';
import Releases from './shell/pages/Releases';
import ResearchArticle from './shell/pages/ResearchArticle';
import ResearchProject from './shell/pages/ResearchProject';
import Users from './shell/pages/Users';
import AppShell from './shell/AppShell';
import ModuleBoundary from './shell/ModuleBoundary';
import ProjectArea from './shell/ProjectArea';
import RequireAuth from './shell/RequireAuth';
import ResumeList from './modules/resume/pages/List';
import ResumeDirectory from './modules/resume/pages/Directory';
import ResumeWorkbench from './modules/resume/pages/Workbench';
import ResumeAdmin from './modules/resume/pages/Admin';
import { modules } from './modules/registry';
import type { PortalModule } from './modules/types';
import { useMe } from './hooks';
import { personaRank } from './shared/persona';

function RequireModulePersona({ module, children }: { module: PortalModule; children: ReactNode }) {
  const me = useMe();
  const persona = me.data?.persona ?? 'visitor';
  if (module.minRole && personaRank(persona) < personaRank(module.minRole)) {
    return (
      <div className="empty">
        <b>没有访问权限</b>
        当前角色不能进入「{module.title}」。
      </div>
    );
  }
  return <>{children}</>;
}

function ModuleRoutes({ module }: { module: PortalModule }) {
  const routes = [
    ...(module.publicRoutes ?? []),
    ...(module.authRoutes ?? []).map((route) => ({
      ...route,
      element: (
        <RequireAuth>
          <RequireModulePersona module={module}>{route.element}</RequireModulePersona>
        </RequireAuth>
      ),
    })),
  ] as RouteObject[];
  const element = useRoutes(routes);
  return element ?? <Navigate to={`/${module.id}`} replace />;
}

export default function App() {
  const active = modules.filter((module) => module.status === 'active');
  const planned = modules.filter((module) => module.status === 'planned');

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/resume" element={<ResumeList />} />
      <Route path="/resume/" element={<ResumeList />} />
      <Route element={<AppShell />}>
        <Route index element={<Home />} />
        {active.map((module) => (
          <Route
            key={module.id}
            path={`/${module.id}/*`}
            element={
              <ModuleBoundary moduleId={module.id}>
                <ModuleRoutes module={module} />
              </ModuleBoundary>
            }
          />
        ))}
        {planned.map((module) => (
          <Route
            key={module.id}
            path={`/${module.id}/*`}
            element={
              <ModuleBoundary moduleId={module.id}>
                <ProjectArea module={module} />
              </ModuleBoundary>
            }
          />
        ))}
        <Route path="/releases" element={<Releases />} />
        <Route path="/research/:projectId" element={<ResearchProject />} />
        <Route path="/research/:projectId/:articleId" element={<ResearchArticle />} />
        <Route path="/admin/users" element={<Users />} />
        <Route
          path="/resumes"
          element={
            <RequireAuth>
              <ResumeDirectory />
            </RequireAuth>
          }
        />
        <Route
          path="/account/resume"
          element={
            <RequireAuth>
              <ResumeWorkbench />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/resumes"
          element={
            <RequireAuth>
              <ResumeAdmin />
            </RequireAuth>
          }
        />
        <Route path="/admin/devices" element={<Navigate to="/ccs/admin/devices" replace />} />
        <Route path="/account/password" element={<Password />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}